/**
 * Desktop Notification Utility
 * Wraps Web Notifications API for browser desktop notifications
 */

import Logger from './logger';

let permissionRequested = false;

/**
 * Check if desktop notifications are supported
 */
export function isNotificationSupported() {
  return 'Notification' in window;
}

/**
 * Get current notification permission status
 */
export function getNotificationPermission() {
  if (!isNotificationSupported()) {
    return 'denied';
  }
  return Notification.permission;
}

/**
 * Request notification permission from user
 */
export async function requestNotificationPermission() {
  if (!isNotificationSupported()) {
    Logger.warn('Desktop notifications not supported in this browser');
    return 'denied';
  }

  if (permissionRequested) {
    return Notification.permission;
  }

  try {
    permissionRequested = true;
    const permission = await Notification.requestPermission();
    Logger.info('Notification permission:', permission);
    return permission;
  } catch (error) {
    Logger.error('Failed to request notification permission', error);
    return 'denied';
  }
}

/**
 * Show a desktop notification
 * @param {string} title - Notification title
 * @param {string} body - Notification body text
 * @param {string} severity - Notification severity (info, warning, error, critical)
 * @param {Function} onClick - Optional click handler
 */
export async function showDesktopNotification(title, body, severity = 'info', onClick = null) {
  if (!isNotificationSupported()) {
    Logger.debug('Desktop notifications not supported');
    return null;
  }

  // Request permission if not already granted
  let permission = Notification.permission;
  if (permission === 'default') {
    permission = await requestNotificationPermission();
  }

  if (permission !== 'granted') {
    Logger.debug('Desktop notification permission not granted');
    return null;
  }

  try {
    const options = {
      body,
      icon: '/rbc-logo.png',
      badge: '/rbc-logo.png',
      tag: `notification-${Date.now()}`,
      requireInteraction: severity === 'critical' || severity === 'error',
      silent: false
    };

    const notification = new Notification(title, options);

    // Handle click event
    notification.onclick = () => {
      window.focus();
      notification.close();

      if (onClick && typeof onClick === 'function') {
        onClick();
      }
    };

    // Auto-close non-critical notifications after 10 seconds
    if (severity !== 'critical') {
      setTimeout(() => {
        notification.close();
      }, 10000);
    }

    Logger.debug('Desktop notification shown', { title, severity });
    return notification;
  } catch (error) {
    Logger.error('Failed to show desktop notification', error);
    return null;
  }
}
